import { NavLink } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, Megaphone, Gift, Network, Mail, MousePointerClick,
  DollarSign, BarChart3, Webhook, LogOut, Link2, Users, Wand2, ListChecks, Menu, X, Map as MapIcon,
} from "lucide-react";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/reports", label: "Reports", icon: BarChart3 },
  { to: "/customers", label: "Customers", icon: Users },
  { to: "/clicks", label: "Clicks Log", icon: MousePointerClick },
  { to: "/click-map", label: "Click Map", icon: MapIcon, owner: true },
  { to: "/conversions", label: "Conversions", icon: DollarSign },
  { to: "/reconciliation", label: "Reconciliation", icon: ListChecks },
  { to: "/campaigns", label: "Campaigns", icon: Megaphone },
  { to: "/offers", label: "Offers", icon: Gift },
  { to: "/networks", label: "Networks", icon: Network },
  { to: "/traffic-sources", label: "Traffic Sources", icon: Mail },
  { to: "/link-generator", label: "Link Generator", icon: Wand2 },
  { to: "/postback-tester", label: "Postback Tester", icon: Webhook },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="flex min-h-screen bg-slate-50">
      <header className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 bg-slate-900 text-white flex items-center justify-between px-4" data-testid="mobile-header">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-indigo-600 flex items-center justify-center">
            <Link2 className="h-3.5 w-3.5 text-white" />
          </div>
          <span className="font-heading font-bold text-sm">Omnilink</span>
        </div>
        <button data-testid="mobile-menu-button" onClick={() => setMenuOpen(!menuOpen)} className="p-2 text-slate-300 hover:text-white">
          {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </header>
      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" data-testid="mobile-overlay" onClick={() => setMenuOpen(false)} />
      )}
      <aside
        className={`w-60 shrink-0 bg-slate-900 text-slate-300 flex flex-col fixed inset-y-0 z-50 transform transition-transform duration-200 ${menuOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
        data-testid="sidebar"
      >
        <div className="px-5 py-5 border-b border-slate-800 flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-lg bg-indigo-600 flex items-center justify-center">
            <Link2 className="h-4 w-4 text-white" />
          </div>
          <div>
            <div className="font-heading font-bold text-white text-sm tracking-tight leading-none">Omnilink</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Tracking Portal</div>
          </div>
          <span className="ml-auto h-2 w-2 rounded-full bg-emerald-400 animate-pulse" title="System live" />
        </div>
        <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto scrollbar-thin">
          {nav.filter((n) => !n.owner || user?.is_owner).map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setMenuOpen(false)}
              data-testid={`nav-${to.slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-medium transition-colors ${
                  isActive ? "bg-indigo-600 text-white" : "hover:bg-slate-800 hover:text-white"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-2.5">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-indigo-600 flex items-center justify-center text-white text-xs font-bold">
                {user?.name?.[0] || "U"}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold text-white truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[10px] text-slate-500 truncate">{user?.email}</div>
            </div>
            <button onClick={logout} data-testid="logout-button" className="text-slate-500 hover:text-white transition-colors" title="Logout">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>
      <main className="flex-1 lg:ml-60 min-w-0 pt-14 lg:pt-0">
        <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]">{children}</div>
      </main>
    </div>
  );
}
